/**
 * The editor's rule for a dropped file: image files are placed where they were
 * dropped, and anything else is left to ProseMirror and the browser.
 *
 * A standalone function over the smallest view interface it needs, rather than a
 * closure inside the editor, because ProseMirror reaches its own drop handler only
 * after `posAtCoords` resolves — which needs layout jsdom does not have. Written
 * this way the rule is testable; inlined it would only be exercisable in a browser.
 */

/** The part of ProseMirror's `EditorView` this rule reads. */
interface DropView {
	posAtCoords: (coords: { left: number; top: number }) => { pos: number } | null
	state: { doc: { content: { size: number } } }
}

/** The image files a drop carries, in the order they were dropped. */
export function droppedImages(transfer: DataTransfer | null): File[] {
	return Array.from(transfer?.files ?? []).filter((file) => file.type.startsWith('image/'))
}

/**
 * Build ProseMirror's `handleDrop` from a handler that places files.
 *
 * Returns `false` for every drop it does not claim, which is what leaves a dragged
 * selection, a dropped link and a text drop behaving exactly as before.
 */
export function imageDropRule(onDropFiles: ((files: File[], position: number) => void) | undefined) {
	return (view: DropView, event: DragEvent, moved: boolean): boolean => {
		// `moved` marks content dragged from inside the document — ProseMirror's own
		// move, never an import.
		if (moved || !onDropFiles) return false

		const files = droppedImages(event.dataTransfer)
		if (files.length === 0) return false

		const at = view.posAtCoords({ left: event.clientX, top: event.clientY })
		// Nothing under the pointer — a drop on the padding past the last line —
		// places at the end of the document rather than discarding the file.
		onDropFiles(files, at?.pos ?? view.state.doc.content.size)

		// Stops the browser's own default, which is to navigate away from the
		// application and open the dropped file.
		event.preventDefault()
		return true
	}
}
